import React, { useRef, useState } from "react";
import { PlusIcon, XIcon, FileIcon } from "./icons";

/**
 * Upload target (E §8): dashed drop area + file chips, camera-capture
 * friendly (`accept`/`capture` pass straight to the hidden input). This
 * component only COLLECTS files — the caller owns the actual upload and
 * passes per-file status via files: [{ id, name, size, status }] where
 * status ∈ "uploading" | "done" | "error".
 */
function formatSize(bytes) {
  if (bytes == null) {
    return "";
  }
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Dropzone({
  label = "Add files",
  accept,
  capture,
  multiple = true,
  files = [],
  onAdd,
  onRemove
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const pick = (fileList) => {
    const picked = Array.from(fileList || []);
    if (picked.length > 0) {
      onAdd(picked);
    }
  };

  return (
    <div>
      <button
        type="button"
        className={`fk-dropzone${dragOver ? " fk-dropzone--over" : ""}`}
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          pick(e.dataTransfer.files);
        }}
      >
        <PlusIcon size={16} />
        {label}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        capture={capture}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={(e) => {
          pick(e.target.files);
          e.target.value = "";
        }}
      />
      {files.length > 0 ? (
        <div className="fk-filechips">
          {files.map((f) => (
            <span
              key={f.id}
              className={`fk-filechip${f.status === "error" ? " fk-filechip--error" : ""}`}
            >
              <FileIcon size={12} />
              <span className="fk-filechip__name">{f.name}</span>
              <span className="fk-filechip__meta">
                {f.status === "uploading"
                  ? "uploading…"
                  : f.status === "error"
                    ? "failed"
                    : formatSize(f.size)}
              </span>
              <button
                type="button"
                className="fk-filechip__x"
                aria-label={`Remove ${f.name}`}
                onClick={() => onRemove(f.id)}
              >
                <XIcon size={11} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default Dropzone;
